import {Form, Formik, FormikProps} from 'formik';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    Button,
    DataWrapper,
    NumericFieldFormik,
    InputFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {dataUtils} from 'utils';

import {CHAT_COMPLETION_PARAMS} from '../constants';
import {AdvancedSettingsFields} from '../types';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.CHAT_GPT.ADVANCED_SETTINGS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FIELDS = {
    MODEL: 'chatCompletionParams.model',
    TEMPERATURE: 'chatCompletionParams.temperature',
    HISTORY_LIMIT: 'historyLimit',
    MAX_TOKENS: 'chatCompletionParams.max_tokens',
    TOP_PROBABILITY: 'chatCompletionParams.top_p',
    FREQUENCY_PENALTY: 'chatCompletionParams.frequency_penalty',
    PRESENCE_PENALTY: 'chatCompletionParams.presence_penalty'
};

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<AdvancedSettingsFields>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, initialValues, submitForm, values
}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-my-2' defaultExpanded={false}>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-ts-h5-bold'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-text-default tw-mt-4'>
                            {t(T_KEYS.DESCRIPTION)}
                        </div>
                        <InputFormik
                            name={FIELDS.MODEL}
                            label={t(T_KEYS.LABELS.MODEL)}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <NumericFieldFormik
                            name={FIELDS.HISTORY_LIMIT}
                            label={t(T_KEYS.LABELS.HISTORY_LIMIT)}
                            decimalScale={CHAT_COMPLETION_PARAMS.HISTORY_LIMIT.DECIMAL_SCALE}
                            allowNegative={CHAT_COMPLETION_PARAMS.HISTORY_LIMIT.ALLOW_NEGATIVE}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <NumericFieldFormik
                            name={FIELDS.TEMPERATURE}
                            label={t(T_KEYS.LABELS.TEMPERATURE)}
                            allowNegative={CHAT_COMPLETION_PARAMS.TEMPERATURE.ALLOW_NEGATIVE}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <NumericFieldFormik
                            name={FIELDS.MAX_TOKENS}
                            label={t(T_KEYS.LABELS.MAX_TOKENS)}
                            decimalScale={CHAT_COMPLETION_PARAMS.MAX_TOKENS.DECIMAL_SCALE}
                            allowNegative={CHAT_COMPLETION_PARAMS.MAX_TOKENS.ALLOW_NEGATIVE}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <NumericFieldFormik
                            name={FIELDS.TOP_PROBABILITY}
                            label={t(T_KEYS.LABELS.TOP_PROBABILITY)}
                            allowNegative={CHAT_COMPLETION_PARAMS.TOP_PROBABILITY.ALLOW_NEGATIVE}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <NumericFieldFormik
                            name={FIELDS.FREQUENCY_PENALTY}
                            label={t(T_KEYS.LABELS.FREQUENCY_PENALTY)}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <NumericFieldFormik
                            name={FIELDS.PRESENCE_PENALTY}
                            label={t(T_KEYS.LABELS.PRESENCE_PENALTY)}
                            className='tw-w-full'
                            spacing='top'
                        />
                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type Props = {
    loading: boolean;
    initialValues: AdvancedSettingsFields;
    onSubmit: (values: AdvancedSettingsFields) => void;
};

export const AdvancedSettings: React.FC<Props> = ({
    loading, initialValues, onSubmit
}) => (
    <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
    >
        {(props) => (
            <FormTemplate
                {...props}
                loading={loading}
            />
        )}
    </Formik>
);